import React, { useContext, useEffect, useRef } from "react";
import { fabric } from "fabric";
import { CanvasContext, ProductContext, ModuleContext } from "../provider";
import comp from ".";
import { AppContext } from "../../../provider/AppProvider";
import ff from "../../../util/fabric";
import { fetchApi } from "../../../config/core/Api";
import ProductControls from "./ProductControls";

const htmlCanvasWrapId = "product-editor-canvas-wrap";
const htmlCanvasId = "product-editor-canvas";
let activeObjectDom = null;

function ProductContainer(): JSX.Element {
  // contexts
  const { printPaper, previewPaper, productDpi, productType } = useContext(
    ProductContext
  );
  const { moduleImages, moduleInfo, areGridLinesEnabled } = useContext(
    ModuleContext
  );
  const { setProductPreview, setIsLoading } = useContext(AppContext);
  const {
    canvasRef,
    setActiveObject,
    originalCanvasSize,
    setOriginalCanvasSize,
    setScaledCanvasSize,
    canvasResizeCoef,
    setCanvasResizeCoef,
  } = useContext(CanvasContext);

  // dom ref
  const canvasWrapRef = useRef<HTMLElement | null>(null);

  /**
   * @method useEffect
   * @memberof ProductContainer
   * @description Hook used to:
   * 1- set canvas wrapper reference.
   * 2- calculate real canvas size
   * 3- calculate initial resize coefficient
   * 4- calculate initial size for scaled canvas
   * 5- initialize canvas,
   * 6- add event listener on window resize in order to scale canvas,
   * 7- add event listener for object moving in canvas.
   * 8- add event listener for object modified in canvas.
   * IMPORTANT: Hook is used only once since printPaper and productDpi are set previously once.
   * Careful editing printPaper and productDpi.
   */
  useEffect(() => {
    if (printPaper && productDpi) {
      // set canvas wrapper reference
      canvasWrapRef.current = document.getElementById(htmlCanvasWrapId);
      window.canvasWrapRef = canvasWrapRef.current;
      // destructure and rename canvas wrapper size
      const {
        width: wrapperWidth,
        height: wrapperHeight,
      } = canvasWrapRef.current.getBoundingClientRect();

      // destructure and rename real canvas size
      const {
        width: originalCanvasWidth,
        height: originalCanvasHeight,
      } = ff.handleCanvasSize(
        productDpi,
        previewPaper.width,
        previewPaper.height
      );
      // set to state original canvas size
      setOriginalCanvasSize({
        width: originalCanvasWidth,
        height: originalCanvasHeight,
      });

      // set resize coefficient and set it to state
      let resizeCoef = wrapperWidth / originalCanvasWidth;
      setCanvasResizeCoef(resizeCoef);

      // set initial new size for canvas
      setScaledCanvasSize({
        width: wrapperWidth,
        height: originalCanvasHeight * resizeCoef,
      });
      window.scaledCanvasSize = {
        width: wrapperWidth,
        height: originalCanvasHeight * resizeCoef,
      };
      // set canvas reference and attack events to it
      canvasRef.current = new fabric.Canvas(htmlCanvasId, {
        // @ts-ignore
        width: window.innerWidth,
        height: originalCanvasHeight * resizeCoef,
        allowTouchScrolling: true,
        backgroundColor: "#ffffff",
        selection: false,
        preserveObjectStacking: true,
      });
      window.canvasRef = canvasRef.current;
      if (canvasRef.current) {
        canvasRef.current.on({
          "object:moving": ff.handleActiveObjectMovement,
          "object:modified": ff.handleActiveObjectModification,
          "touch:drag": function() {},
          "mouse:up": handleClickEvent,
          "selection:created": disableScroll,
          "mouse:down": function(event: any) {
            if (event.target) {
              disableScroll();
            } else {
              enableScroll();
            }
          },
          "selection:cleared": enableScroll,
        });
      }
    }
  }, [printPaper, productDpi]);

  function disableScroll() {
    window.canvasRef.allowTouchScrolling = false;
  }

  function enableScroll() {
    window.canvasRef.allowTouchScrolling = true;
  }

  function handleClickEvent(e: fabric.IEvent) {
    // Hide image type uploader in any case of click in canvas
    if (e.target) {
      // specific for TEXT edit tool

      if (activeObjectDom === e.target || !e.target.selectable) {
        setActiveObject(null);
        activeObjectDom = null;
        ff.clearActiveObjects(canvasRef.current);
      } else if (e.target.selectable) {
        activeObjectDom = e.target;
        setActiveObject(e.target);
      }
    } else {
      activeObjectDom = null;
      setActiveObject(null);
      ff.clearActiveObjects(canvasRef.current);
    }
  }

  function sendSvgToServer() {
    let tempImages = [];
    if (moduleImages || (moduleInfo.preset && moduleInfo.preset.images)) {
      if (moduleImages) {
        moduleImages.map((img) => {
          tempImages.push(img.renderUrl);
        });
      } else {
        moduleInfo.preset.images.map((img) => {
          tempImages.push(img.renderUrl);
        });
      }
    }
    // add grid lines for some modules
    if (areGridLinesEnabled) {
      // @ts-ignore
      ff.handleLinesForImageGrid(canvasRef.current, canvasRef.current.padding);
    }

    setIsLoading(true);
    fetchApi({
      method: "POST",
      url: "?action=generate_paper",
      data: {
        customAttributes: {
          ...originalCanvasSize,
          paper: {
            ...printPaper,
          },
          images: tempImages,
          bgColor: productType == "kids" ? "#ffffff" : moduleInfo.bgColor,
          paperPadding:
            productType == "only-image"
              ? ff.cmToPx(0.3, printPaper.dpi) * canvasResizeCoef
              : 0,
        },
        fabricSvg: canvasRef.current.toSVG({
          // @ts-ignore
          width: "100%",
          // @ts-ignore
          height: "100%",
        }),
      },
      timeout: 300000,
    })
      .then((r) => {
        setProductPreview(r.data);
        window.scrollTo(0, 1);
      })
      .catch(console.log)
      .finally(() => {
        setIsLoading(false);
      });
  }

  return (
    <div className={"position-relative p-1"}>
      <div className={"product-canvas"}>
        <div
          id={htmlCanvasWrapId}
          style={{
            overflow: "hidden",
            position: "relative" as "relative",
            borderRadius: 6,
          }}>
          <comp.CanvasScrollLock />
          <comp.CanvasHtmlElement htmlCanvasId={htmlCanvasId} />
          <comp.CanvasControls />
        </div>
      </div>
      <ProductControls preview={sendSvgToServer} />
    </div>
  );
}

export default ProductContainer;
